import { ChangeEvent, useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';

import styles from './AddBrand.module.scss';
import { IAddBrandProps } from './IAddBrandProps';
import { BrandRequestModel } from '../../../models/brands/BrandRequestModel';
import { HttpService, IHttpService } from '../../../services/httpService';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import {
  INotifierService,
  NotifierService,
} from '../../../services/notifierService';

const AddBrand = ({
  brandTypesOptions,
  setShouldReload,
}: IAddBrandProps): JSX.Element => {
  const [code, setCode] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [brandTypeId, setBrandTypeId] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const httpService: IHttpService = useMemo(() => new HttpService(), []);
  const notifierService: INotifierService = useMemo(
    () => new NotifierService(),
    [],
  );

  const resetDialog = (): void => {
    setCode('');
    setIsVisible(false);
    setIsLoading(false);
  };

  const handleAddBrand = async (): Promise<void> => {
    setIsLoading(true);

    const brand: BrandRequestModel = {
      code,
      description,
      brandTypeId,
    };

    try {
      await httpService.post('brands', JSON.stringify(brand), undefined, false);

      setShouldReload(true);
      resetDialog();
    } catch (err) {
      notifierService.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const addDialogFooter: JSX.Element = (
    <>
      <button
        className="btn primary"
        onClick={handleAddBrand}
        disabled={!code || !description || !brandTypeId}
      >
        Add
      </button>
      <button className="btn secondary" onClick={resetDialog}>
        Cancel
      </button>
    </>
  );

  const pendingFooter: JSX.Element = (
    <div className={styles.dialogFooter}>
      <p>Creating Brand...</p>
      <ProgressSpinner />
    </div>
  );

  return (
    <>
      <button
        className={`${styles.addButton} btn primary`}
        onClick={() => setIsVisible(true)}
      >
        Add Brand
      </button>

      <Dialog
        className={styles.dialog}
        header="Add Brand"
        visible={isVisible}
        onHide={resetDialog}
        footer={isLoading ? pendingFooter : addDialogFooter}
        baseZIndex={9999}
      >
        <div className={styles.dialogContent}>
          <label htmlFor="code">Code</label>
          <InputText
            id="code"
            placeholder="Code"
            value={code}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setCode(e.target.value);
            }}
          />
        </div>

        <div className={styles.dialogContent}>
          <label htmlFor="description">Description</label>
          <InputText
            id="description"
            placeholder="Description"
            value={description}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setDescription(e.target.value);
            }}
          />
        </div>

        <div className={styles.dialogContent}>
          <label htmlFor="brandTypeId">Brand Type</label>
          <Dropdown
            id="brandTypeId"
            placeholder="Select Brand Type"
            value={brandTypeId}
            options={brandTypesOptions}
            onChange={(e: DropdownChangeParams) => {
              setBrandTypeId(e.target.value);
            }}
          />
        </div>
      </Dialog>
    </>
  );
};

export default AddBrand;
